import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  AlertCircle, Archive, AtSign, Bell, BellOff, Bookmark,
  Check, CheckCheck, ChevronLeft, ChevronRight, Clock,
  Copy, Download, Eye, FileText, Filter, Key, Lock, Mail,
  MessageCircle, MoreVertical, Paperclip, Reply,
  ReplyAll, RotateCw, Search, Send, Shield, Smartphone, Star,
  Tag, Trash2, Upload, X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../config/axiosConfig';
import messagerieSocket from '../../socket/messagerieSocket';
import usePesacoreIH from '../../stores/usePesacore';

// ─── Store bridge ─────────────────────────────────────────────────────────────
function useMessagerieStore() {
  const { messagerieOpen, messagerieRecipient, closeMessagerie, sendInternalMessage } = usePesacoreIH();
  return { isOpen: messagerieOpen, recipient: messagerieRecipient, closeMessagerie, sendInternalMessage };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getPriorityIcon(priority) {
  if (priority === 'urgent') return <AlertCircle size={12} className="text-red-500 shrink-0" />;
  if (priority === 'high')   return <AlertCircle size={12} className="text-orange-500 shrink-0" />;
  return null;
}

function getTypeIcon(type, size = 14) {
  switch (type) {
    case 'credentials':  return <Key    size={size} className="text-orange-400" />;
    case 'notification': return <Bell   size={size} className="text-sky-400"    />;
    case 'alert':        return <Shield size={size} className="text-red-400"    />;
    case 'email':        return <AtSign size={size} className="text-violet-400" />;
    default:             return <Mail   size={size} className="text-zinc-500"   />;
  }
}

function getReadIcon(msg) {
  if (msg.readAt || msg.isRead)     return <CheckCheck size={13} className="text-emerald-500 shrink-0" />;
  if (msg.status === 'delivered')   return <Check      size={13} className="text-zinc-500 shrink-0"    />;
  return <Clock size={13} className="text-zinc-600 shrink-0" />;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    const date = new Date(d);
    // Vérifier si la date est valide
    if (isNaN(date.getTime())) return '—';
    const diff = Math.floor((Date.now() - date) / 86_400_000);
    if (diff === 0) return format(date, 'HH:mm', { locale: fr });
    if (diff === 1) return 'Hier';
    if (diff < 7)  return format(date, 'EEE', { locale: fr });
    return format(date, 'dd MMM', { locale: fr });
  } catch {
    return '—';
  }
}

// Normalise InboundEmail + message interne → shape UI commune
function normalizeMsg(m, canal) {
  if (canal === 'email') {
    return {
      _id:        m._id,
      canal:      'email',
      subject:    m.subject   || '(sans sujet)',
      body:       m.bodyText  || '',
      bodyHtml:   m.bodyHtml  || '',
      from:       m.from      || { name: '', email: '' },
      to:         m.to        || [],
      cc:         m.cc        || [],
      isRead:     m.isRead    || false,
      isStarred:  m.isStarred || false,
      isArchived: m.isArchived || false,
      labels:     m.labels    || [],
      attachments:m.attachments || [],
      date:       m.receivedAt || m.sentAt || m.createdAt,
      flags:      m.flags     || [],
      type:       'email',
    };
  }
  return {
    _id:        m._id,
    canal:      'interne',
    subject:    m.subject  || '(sans sujet)',
    body:       m.body     || '',
    bodyHtml:   '',
    from:       m.from     || { name: '', email: '', type: '' },
    to:         m.to       || [],
    isRead:     m.status === 'read' || !!m.readAt,
    isStarred:  m.isStarred || false,
    isArchived: false,
    labels:     m.labels   || [],
    attachments:m.attachments || [],
    date:       m.sentAt   || m.createdAt,
    flags:      [],
    type:       m.messageType || 'conversation',
    threadId:   m.threadId,
    metadata:   m.metadata || {},
    status:     m.status,
  };
}

// ─── Constantes ───────────────────────────────────────────────────────────────
const TEMPLATES = [
  { id: 'welcome',  name: 'Bienvenue', subject: 'Bienvenue sur la plateforme',   body: 'Bonjour,\n\nNous sommes ravis de vous accueillir sur la plateforme Pesacore.' },
  { id: 'reminder', name: 'Rappel',    subject: 'Rappel important',              body: 'Bonjour,\n\nCeci est un rappel concernant...' },
  { id: 'followup', name: 'Suivi',     subject: 'Suivi de notre échange',        body: 'Bonjour,\n\nJe reviens vers vous concernant notre dernier échange.' },
];

const LABELS = [
  { id: 'urgent',    name: 'Urgent',    cls: 'text-red-400     border-red-800/40     bg-red-950/20'     },
  { id: 'important', name: 'Important', cls: 'text-orange-400  border-orange-800/40  bg-orange-950/20'  },
  { id: 'later',     name: 'Plus tard', cls: 'text-blue-400    border-blue-800/40    bg-blue-950/20'    },
  { id: 'done',      name: 'Traité',    cls: 'text-emerald-400 border-emerald-800/40 bg-emerald-950/20' },
];

const EMPTY_COMPOSE = {
  canal: 'interne',
  to: [], cc: [], bcc: [], subject: '', body: '',
  priority: 'normal', attachments: [],
  template: null, encrypted: false, requireReadReceipt: false,
};

// Gestion centralisée des brouillons (localStorage)
const DRAFTS_KEY = 'ih_messagerie_drafts';
function getDrafts() {
  try {
    const raw = localStorage.getItem(DRAFTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}
function saveDraft(draft) {
  const drafts = getDrafts();
  const index = drafts.findIndex(d => d.id === draft.id);
  if (index >= 0) drafts[index] = draft;
  else drafts.push(draft);
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
}
function deleteDraft(id) {
  const drafts = getDrafts().filter(d => d.id !== id);
  localStorage.setItem(DRAFTS_KEY, JSON.stringify(drafts));
}

// ─── DestinataireInput (permet saisie libre + autocomplétion) ─────────────────
function DestinataireInput({
  label,
  recipients = [],
  onChange,
  canal = 'interne',
  placeholder = "Saisir un email ou sélectionner..."
}) {
  const [inputValue, setInputValue] = useState('');
  const [showSelector, setShowSelector] = useState(false);
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/users/me/profiles');
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Erreur chargement users:', err);
      } finally {
        setLoading(false);
      }
    };
    if (showSelector) fetchUsers();
  }, [showSelector]);

  const addRecipient = (value) => {
    if (!value || !value.trim()) return;
    const trimmed = value.trim();
    if (recipients.includes(trimmed)) return;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (canal === 'email') {
      // Mode EMAIL : toujours stocker l'adresse email
      if (emailRegex.test(trimmed)) {
        onChange([...recipients, trimmed]);
      } else {
        // Tenter de trouver l'utilisateur par nom ou email
        const user = users.find(u =>
          `${u.prenom} ${u.nom}`.toLowerCase().includes(trimmed.toLowerCase()) ||
          u.email.toLowerCase().includes(trimmed.toLowerCase())
        );
        if (user) {
          onChange([...recipients, user.email]);
        } else {
          // Si aucun utilisateur trouvé mais le format n'est pas email -> invalide
          console.warn('Email invalide:', trimmed);
          return;
        }
      }
    } else {
      // Mode INTERNE : stocker l'ID utilisateur
      const user = users.find(u =>
        `${u.prenom} ${u.nom}`.toLowerCase().includes(trimmed.toLowerCase()) ||
        u.email.toLowerCase().includes(trimmed.toLowerCase())
      );
      if (user) {
        onChange([...recipients, user._id]);
      } else {
        console.warn('Utilisateur non trouvé:', trimmed);
        return;
      }
    }
    setInputValue('');
    setSearch('');
  };

  const removeRecipient = (recipient) => {
    onChange(recipients.filter(r => r !== recipient));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && inputValue) {
      e.preventDefault();
      addRecipient(inputValue);
    }
    if (e.key === ',' && inputValue) {
      e.preventDefault();
      addRecipient(inputValue);
    }
  };

  const handleSelectUser = (user) => {
    const value = canal === 'email' ? user.email : user._id;
    if (!recipients.includes(value)) {
      onChange([...recipients, value]);
    }
    setShowSelector(false);
    setInputValue('');
  };

  const getDisplayValue = (recipient) => {
    if (canal === 'email') {
      const user = users.find(u => u.email === recipient);
      return user ? `${user.prenom} ${user.nom} <${recipient}>` : recipient;
    } else {
      const user = users.find(u => u._id === recipient);
      return user ? `${user.prenom} ${user.nom} (${user.email})` : recipient;
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-[10px] uppercase text-zinc-500 mb-1.5">
        {label} *
      </label>

      {recipients.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {recipients.map(recipient => (
            <span
              key={recipient}
              className="inline-flex items-center gap-1.5 px-2 py-1 bg-sky-950/40 border border-sky-700/40 rounded-lg text-xs text-sky-300"
            >
              {getDisplayValue(recipient)}
              <button
                onClick={() => removeRecipient(recipient)}
                className="hover:text-red-400 transition-colors"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowSelector(true)}
              placeholder={placeholder}
              className="w-full bg-zinc-950 border border-zinc-700/70 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-sky-500 text-zinc-200"
            />
            {inputValue && (
              <button
                onClick={() => addRecipient(inputValue)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-emerald-400 hover:text-emerald-300"
              >
                <Send size={12} />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => setShowSelector(!showSelector)}
            className="px-3 py-2.5 bg-zinc-900 border border-zinc-700 rounded-xl text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            <ChevronRight size={14} className={`transition-transform ${showSelector ? 'rotate-90' : ''}`} />
          </button>
        </div>

        {showSelector && (
          <div className="absolute z-50 left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden">
            <div className="p-2 border-b border-zinc-800">
              <div className="relative">
                <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Rechercher un utilisateur..."
                  className="w-full pl-8 pr-3 py-2 text-xs bg-zinc-950 border border-zinc-700 rounded-lg text-zinc-300 outline-none focus:border-sky-500"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto">
              {loading ? (
                <div className="text-center py-8 text-zinc-600 text-xs">Chargement...</div>
              ) : (
                users
                  .filter(u =>
                    !search.trim() ||
                    `${u.prenom} ${u.nom}`.toLowerCase().includes(search.toLowerCase()) ||
                    u.email.toLowerCase().includes(search.toLowerCase())
                  )
                  .map(user => {
                    const value = canal === 'email' ? user.email : user._id;
                    const isSelected = recipients.includes(value);
                    return (
                      <div
                        key={user._id}
                        onClick={() => handleSelectUser(user)}
                        className={`flex items-center gap-3 p-3 cursor-pointer transition-colors border-l-2 ${
                          isSelected 
                            ? 'bg-sky-950/20 border-l-sky-500' 
                            : 'border-l-transparent hover:bg-zinc-800/50'
                        }`}
                      >
                        <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs font-bold shrink-0">
                          {(user.prenom?.[0] || user.nom?.[0] || '?').toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-zinc-200 truncate">
                            {user.prenom} {user.nom}
                          </div>
                          <div className="text-[10px] text-zinc-500 font-mono truncate">{user.email}</div>
                        </div>
                        {isSelected && <Check size={14} className="text-emerald-400 shrink-0" />}
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ContactSelector (simplifié, utilisé dans ComposePanel) ─────────────────
function ContactSelector({ selected, onSelect, multiple = true, canal = 'interne', showToast }) {
  const [search, setSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const roleColors = {
    superadmin:     'border-purple-500/50 bg-purple-950/20 text-purple-300',
    admin:          'border-sky-500/50   bg-sky-950/20   text-sky-300',
    entreprise:     'border-emerald-500/50 bg-emerald-950/20 text-emerald-300',
    collectivité:   'border-blue-500/50 bg-blue-950/20 text-blue-300',
    établissement:  'border-indigo-500/50 bg-indigo-950/20 text-indigo-300',
    pro:            'border-amber-500/50 bg-amber-950/20 text-amber-300',
    learner:        'border-zinc-500/50 bg-zinc-950/20 text-zinc-400',
    particulier:    'border-zinc-500/50 bg-zinc-950/20 text-zinc-400',
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/users/me/profiles');
        setUsers(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('[ContactSelector] Erreur chargement users:', err);
        showToast?.('Erreur chargement des contacts', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(u => 
      u.nom?.toLowerCase().includes(q) ||
      u.prenom?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q)
    );
  }, [users, search]);

  const displaySelected = useMemo(() => {
    if (selected.length === 0) return null;
    if (selected.length === 1) {
      const u = users.find(u => (canal === 'email' ? u.email === selected[0] : u._id === selected[0]));
      return u ? `${u.prenom} ${u.nom} (${u.role})` : selected[0];
    }
    return `${selected.length} destinataire(s) sélectionné(s)`;
  }, [selected, users, canal]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-zinc-950 border border-zinc-700/70 rounded-xl text-sm text-zinc-300 hover:border-sky-500/50 transition-all"
      >
        <span className="truncate">
          {displaySelected || 'Sélectionner des destinataires...'}
        </span>
        <ChevronRight size={14} className={`text-zinc-500 transition-transform ${dropdownOpen ? 'rotate-90' : ''}`} />
      </button>

      {dropdownOpen && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden">
          <div className="p-2 border-b border-zinc-800">
            <div className="relative">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Rechercher (nom, email, rôle)..."
                className="w-full pl-8 pr-3 py-2 text-xs bg-zinc-950 border border-zinc-700 rounded-lg text-zinc-300 outline-none focus:border-sky-500"
                autoFocus
              />
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8 gap-2 text-zinc-600">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-sky-500" />
                <span className="text-xs">Chargement...</span>
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-8 text-zinc-600 text-xs">Aucun utilisateur trouvé</div>
            ) : (
              filteredUsers.map(user => {
                const isSelected = selected.some(s => 
                  canal === 'email' ? s === user.email : s === user._id
                );
                const roleClass = roleColors[user.role] || 'border-zinc-600 bg-zinc-800/30 text-zinc-400';
                
                return (
                  <div
                    key={user._id}
                    onClick={() => {
                      if (multiple) {
                        onSelect(user, !isSelected);
                      } else {
                        onSelect(user, true);
                        setDropdownOpen(false);
                      }
                    }}
                    className={`flex items-center gap-3 p-3 cursor-pointer transition-all border-l-2 ${
                      isSelected 
                        ? `bg-sky-950/20 border-l-sky-500 ${roleClass}`
                        : 'border-l-transparent hover:bg-zinc-800/50'
                    }`}
                  >
                    {user.photoUrl ? (
                      <img src={user.photoUrl} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-xs font-bold shrink-0">
                        {(user.prenom?.[0] || user.nom?.[0] || '?').toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-zinc-200 truncate">
                          {user.prenom} {user.nom}
                        </span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${roleClass}`}>
                          {user.role}
                        </span>
                        {user.pesaRole && (
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-orange-800/40 bg-orange-950/20 text-orange-400">
                            {user.pesaRole}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] text-zinc-500 font-mono truncate">{user.email}</div>
                    </div>
                    {isSelected && <Check size={14} className="text-emerald-400 shrink-0" />}
                  </div>
                );
              })
            )}
          </div>
          {multiple && selected.length > 0 && (
            <div className="p-2 border-t border-zinc-800 flex justify-between items-center bg-zinc-950/50">
              <span className="text-[10px] text-zinc-500">{selected.length} sélectionné(s)</span>
              <button
                onClick={() => onSelect(null, false, true)}
                className="text-[10px] text-red-400 hover:text-red-300"
              >
                Tout effacer
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── MessageRow (inchangé) ───────────────────────────────────────────────────
function MessageRow({ msg, canal, isSelected, onSelect, onStar }) {
  const senderName = typeof msg.from === 'object'
    ? (msg.from?.type === 'system' ? '⚙ Système' : (msg.from?.name || msg.from?.email || '?'))
    : String(msg.from || '?');
  const isUnread = !msg.isRead;
  const isSentMessage = msg.type === 'sent' || (msg.canal === 'email' && msg.from?.email === process.env.REACT_APP_SUPERADMIN_EMAIL);

  const displayInfo = isSentMessage && Array.isArray(msg.to) && msg.to.length > 0
    ? `→ ${msg.to.slice(0, 2).map(t => typeof t === 'object' ? (t.name || t.email || '?') : String(t)).join(', ')}${msg.to.length > 2 ? ` +${msg.to.length - 2}` : ''}`
    : senderName;

  return (
    <div onClick={() => onSelect(msg)} className={`cursor-pointer transition-colors ${isUnread ? 'bg-sky-950/10' : ''} ${
      isSelected
        ? canal === 'email' ? 'bg-violet-900/20 border-l-2 border-violet-500' : 'bg-sky-900/20 border-l-2 border-sky-500'
        : 'hover:bg-zinc-800/20'
    }`}>
      <div className="md:hidden px-4 py-3 flex items-start gap-3">
        <button onClick={e => onStar(msg._id, e)} className="mt-1 shrink-0">
          <Star size={12} className={msg.isStarred ? 'text-amber-400 fill-amber-400' : 'text-zinc-700'} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span className={`text-xs truncate ${isUnread ? 'font-bold text-zinc-200' : 'text-zinc-500'}`}>{displayInfo}</span>
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-[10px] text-zinc-700 font-mono">{fmtDate(msg.date)}</span>
              {getReadIcon(msg)}
            </div>
          </div>
          <div className={`text-xs truncate ${isUnread ? 'text-zinc-300 font-medium' : 'text-zinc-600'}`}>
            {msg.attachments?.length > 0 && <Paperclip size={9} className="inline mr-1 text-zinc-700" />}
            {String(msg.subject)}
          </div>
          <div className="text-[10px] text-zinc-700 truncate mt-0.5">{String(msg.body).substring(0, 60)}</div>
        </div>
        <div className="shrink-0 mt-0.5">{getTypeIcon(msg.type, 12)}</div>
      </div>

      <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 items-center group">
        <div className="col-span-1 flex justify-center">
          <button onClick={e => onStar(msg._id, e)} className="opacity-0 group-hover:opacity-100 transition-opacity">
            <Star size={13} className={msg.isStarred ? 'text-amber-400 fill-amber-400' : 'text-zinc-600 hover:text-amber-400'} />
          </button>
        </div>
        <div className="col-span-1 flex justify-center">{getTypeIcon(msg.type, 13)}</div>
        <div className="col-span-4 flex items-center gap-1.5 min-w-0">
          {getPriorityIcon(msg.metadata?.priority)}
          <span className={`text-xs truncate ${isUnread ? 'font-bold text-zinc-200' : 'text-zinc-500'}`}>{displayInfo}</span>
        </div>
        <div className="col-span-4 min-w-0">
          <div className="flex items-center gap-1">
            {msg.attachments?.length > 0 && <Paperclip size={10} className="text-zinc-600 shrink-0" />}
            <span className={`text-xs truncate ${isUnread ? 'font-semibold text-zinc-200' : 'text-zinc-500'}`}>{String(msg.subject)}</span>
          </div>
          <div className="text-[10px] text-zinc-700 truncate">{String(msg.body).substring(0, 45)}</div>
        </div>
        <div className="col-span-2 flex flex-col items-end gap-0.5">
          <span className="text-[10px] text-zinc-600 font-mono">{fmtDate(msg.date)}</span>
          {getReadIcon(msg)}
        </div>
      </div>
    </div>
  );
}

// ─── MessageDetail (avec Reply All, Transfer) ────────────────────────────────
function MessageDetail({
  msg, canal, threadMsgs, showThread, setShowThread,
  replyMode, setReplyMode, replyBody, setReplyBody,
  replyAllMode, setReplyAllMode,
  typingUsers, openDropdown, setOpenDropdown,
  onClose, onStar, onAddLabel, onArchive, onDelete, onSendSMS, onReply, onReplyAll, onTransfer,
}) {
  const senderName = typeof msg.from === 'object'
    ? (msg.from?.type === 'system' ? 'Système' : (msg.from?.name || msg.from?.email || '?'))
    : String(msg.from || '?');
  const isSystem = msg.from?.type === 'system' || msg.from?.userId === 'system';
  const isSentMessage = msg.type === 'sent' || (msg.canal === 'email' && msg.from?.email === process.env.REACT_APP_SUPERADMIN_EMAIL);

  const displayRecipients = isSentMessage && Array.isArray(msg.to) && msg.to.length > 0
    ? msg.to.map(t => typeof t === 'object' ? (t.name || t.email || '?') : String(t)).join(', ')
    : null;

  // Pour Reply All : construire la liste des destinataires (tous sauf soi-même)
  const allRecipients = useMemo(() => {
    if (!msg.to) return [];
    const all = [...msg.to];
    if (msg.from?.email && msg.from.email !== process.env.REACT_APP_SUPERADMIN_EMAIL) {
      all.push(msg.from.email);
    }
    if (msg.cc) all.push(...msg.cc);
    // Déduplication simple par email
    return [...new Set(all.map(t => typeof t === 'object' ? t.email : t).filter(Boolean))];
  }, [msg]);

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="shrink-0 px-4 py-3 border-b border-zinc-800/60 bg-zinc-950/40">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            {getTypeIcon(msg.type, 14)}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-zinc-200 leading-snug break-words">{String(msg.subject)}</div>
              <div className="text-[11px] text-zinc-600 mt-0.5">
                {isSentMessage ? (
                  <>
                    <span className="text-zinc-500">À :</span>
                    <span className="text-zinc-400 ml-1">{displayRecipients}</span>
                  </>
                ) : (
                  <>
                    <span className="text-zinc-500">De :</span>
                    <span className="text-zinc-400 ml-1">{senderName}</span>
                    {canal === 'email' && msg.from?.email && (
                      <span className="ml-1 font-mono text-zinc-600">&lt;{msg.from.email}&gt;</span>
                    )}
                  </>
                )}
                <span className="ml-2">{fmtDate(msg.date)}</span>
              </div>
            </div>
          </div>
          <button onClick={onClose}
            className="shrink-0 p-1.5 hover:bg-red-900/20 hover:text-red-400 border border-zinc-700/60 hover:border-red-800/40 rounded-lg transition-colors text-zinc-600">
            <X size={14} />
          </button>
        </div>

        <div className="flex items-center gap-1 flex-wrap">
          {threadMsgs && (
            <button onClick={() => setShowThread(v => !v)}
              className={`p-1.5 rounded-lg border text-xs transition-all ${showThread ? 'bg-purple-600/20 border-purple-500/30 text-purple-400' : 'border-zinc-700/60 hover:bg-zinc-800 text-zinc-600'}`}>
              <MessageCircle size={13} />
            </button>
          )}
          <button onClick={e => onStar(msg._id, e)}
            className="p-1.5 hover:bg-zinc-800 border border-zinc-700/60 rounded-lg transition-colors">
            <Star size={13} className={msg.isStarred ? 'text-amber-400 fill-amber-400' : 'text-zinc-600'} />
          </button>

          <div className="relative" onMouseDown={e => e.stopPropagation()}>
            <button onClick={() => setOpenDropdown(d => d === 'labels' ? null : 'labels')}
              className="p-1.5 hover:bg-zinc-800 border border-zinc-700/60 rounded-lg transition-colors text-zinc-600">
              <Tag size={13} />
            </button>
            {openDropdown === 'labels' && (
              <div className="absolute left-0 top-full mt-1 w-40 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 py-1">
                {LABELS.map(l => (
                  <button key={l.id} onClick={() => onAddLabel(msg._id, l.id)}
                    className={`w-full px-3 py-2 text-left text-xs hover:bg-zinc-800 flex items-center gap-2 ${l.cls.split(' ')[0]}`}>
                    <div className={`w-1.5 h-1.5 rounded-full border ${l.cls.split(' ')[1]} ${l.cls.split(' ')[2]}`} />
                    {l.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative" onMouseDown={e => e.stopPropagation()}>
            <button onClick={() => setOpenDropdown(d => d === 'more' ? null : 'more')}
              className="p-1.5 hover:bg-zinc-800 border border-zinc-700/60 rounded-lg transition-colors text-zinc-600">
              <MoreVertical size={13} />
            </button>
            {openDropdown === 'more' && (
              <div className="absolute left-0 top-full mt-1 w-48 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 py-1">
                <button onClick={() => onArchive(msg._id)} className="w-full px-4 py-2.5 text-left text-xs hover:bg-zinc-800 text-zinc-300 flex items-center gap-2"><Archive size={13} /> Archiver</button>
                <button onClick={() => onDelete(msg._id)}  className="w-full px-4 py-2.5 text-left text-xs hover:bg-zinc-800 text-red-400   flex items-center gap-2"><Trash2  size={13} /> Supprimer</button>
                {!isSystem && (
                  <button onClick={() => { const p = window.prompt('N° téléphone :'); if (p) onSendSMS(p, `Re: ${msg.subject}\n\n${msg.body.substring(0, 100)}...`); }}
                    className="w-full px-4 py-2.5 text-left text-xs hover:bg-zinc-800 text-emerald-400 flex items-center gap-2"><Smartphone size={13} /> Envoyer SMS</button>
                )}
              </div>
            )}
          </div>

          {!replyMode && !replyAllMode && !isSystem && (
            <>
              <button onClick={() => setReplyMode(true)}
                className="p-1.5 hover:bg-zinc-800 border border-zinc-700/60 rounded-lg transition-colors text-zinc-600">
                <Reply size={13} />
              </button>
              {!isSentMessage && allRecipients.length > 0 && (
                <button onClick={() => setReplyAllMode(true)}
                  className="p-1.5 hover:bg-zinc-800 border border-zinc-700/60 rounded-lg transition-colors text-zinc-600">
                  <ReplyAll size={13} />
                </button>
              )}
              <button onClick={() => onTransfer(msg)}
                className="p-1.5 hover:bg-zinc-800 border border-zinc-700/60 rounded-lg transition-colors text-zinc-600">
                <Copy size={13} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {showThread && threadMsgs ? (
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-zinc-800/50">
              <MessageCircle size={13} className="text-purple-400" />
              <span className="text-xs font-bold text-purple-300 uppercase tracking-wide">Conversation ({threadMsgs.length})</span>
            </div>
            {threadMsgs.map(m => (
              <div key={m._id} className={`p-4 rounded-xl border ${m._id === msg._id ? 'bg-sky-950/20 border-sky-800/40' : 'bg-zinc-950/30 border-zinc-800/30'}`}>
                <div className="flex items-center justify-between mb-3 pb-2 border-b border-zinc-800/30">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-sky-500/10 flex items-center justify-center text-sky-400 text-[9px] font-bold border border-sky-500/20">
                      {(m.from?.name || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div className="text-xs font-medium text-zinc-300">{m.from?.name || m.from?.email || '?'}</div>
                      <div className="text-[10px] text-zinc-700">{fmtDate(m.date)}</div>
                    </div>
                  </div>
                  {getReadIcon(m)}
                </div>
                <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{String(m.body)}</p>
              </div>
            ))}
            {typingUsers.size > 0 && (
              <div className="flex items-center gap-2 text-[10px] text-zinc-600 italic">
                <div className="flex gap-1">{[0,150,300].map(d => <div key={d} className="w-1.5 h-1.5 bg-sky-500 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />)}</div>
                En train d'écrire...
              </div>
            )}
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="flex flex-wrap items-center gap-2.5 text-[10px] text-zinc-600 pb-4 border-b border-zinc-800/40">
              <span className="flex items-center gap-1"><Clock size={11} />{fmtDate(msg.date)}</span>
              {msg.metadata?.priority && msg.metadata.priority !== 'normal' && (
                <span className={`px-2 py-0.5 border text-[9px] font-bold uppercase rounded ${msg.metadata.priority === 'urgent' ? 'border-red-500/30 text-red-400' : 'border-orange-500/30 text-orange-400'}`}>{msg.metadata.priority}</span>
              )}
              {msg.metadata?.encrypted && <span className="flex items-center gap-1 text-emerald-400"><Lock size={11} />Chiffré</span>}
              {msg.labels?.length > 0 && (
                <div className="flex gap-1 flex-wrap">
                  {msg.labels.map(lid => {
                    const l = LABELS.find(x => x.id === lid);
                    return l ? <span key={lid} className={`text-[9px] px-2 py-0.5 rounded-full border ${l.cls}`}>{l.name}</span> : null;
                  })}
                </div>
              )}
            </div>

            {msg.type === 'sent' && Array.isArray(msg.to) && msg.to.length > 0 && (
              <div className="mb-3 pb-2 border-b border-zinc-800/40">
                <div className="flex flex-wrap items-start gap-2">
                  <span className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider shrink-0">À :</span>
                  <div className="flex flex-wrap gap-1.5 flex-1">
                    {msg.to.map((dest, idx) => {
                      const destEmail = typeof dest === 'object' ? (dest.email || dest.name) : String(dest);
                      const destName = typeof dest === 'object' ? dest.name : '';
                      return (
                        <span key={idx} className="inline-flex items-center gap-1.5 px-2 py-1 bg-zinc-800/50 rounded-lg text-xs">
                          {destName && <span className="text-zinc-300">{destName}</span>}
                          {destEmail && destEmail !== destName && (
                            <span className="text-[10px] text-zinc-500 font-mono">{destEmail}</span>
                          )}
                          {!destName && !destEmail && <span className="text-zinc-500">{String(dest)}</span>}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {msg.bodyHtml ? (
              <div className="text-sm text-zinc-300 leading-relaxed prose prose-invert prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: msg.bodyHtml }} />
            ) : (
              <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap">{String(msg.body)}</p>
            )}

            {msg.type === 'credentials' && msg.metadata?.tempPassword && (
              <div className="p-4 bg-orange-950/20 border border-orange-800/40 rounded-xl">
                <div className="flex items-center gap-2 mb-3"><Key size={13} className="text-orange-400" /><span className="text-xs font-bold text-orange-300">Identifiants</span></div>
                <div className="space-y-1 text-xs font-mono">
                  <div><span className="text-zinc-600">Email : </span><span className="text-zinc-200">{msg.metadata.email || '—'}</span></div>
                  <div><span className="text-zinc-600">MDP : </span><span className="text-orange-400 font-bold bg-orange-950/30 px-1.5 py-0.5 rounded">{msg.metadata.tempPassword}</span></div>
                </div>
              </div>
            )}

            {msg.attachments?.length > 0 && (
              <div className="p-4 bg-zinc-950/40 border border-zinc-800/50 rounded-xl">
                <div className="flex items-center gap-2 mb-3"><Paperclip size={13} className="text-sky-400" /><span className="text-xs font-bold text-sky-300">Pièces jointes ({msg.attachments.length})</span></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {msg.attachments.map((att, i) => (
                    <a key={i} href={att.url} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2.5 p-3 bg-zinc-900/50 border border-zinc-700 rounded-xl hover:bg-zinc-800 group">
                      <Paperclip size={12} className="text-zinc-600 group-hover:text-sky-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-zinc-300 truncate">{att.filename || att.name}</div>
                        {att.size && <div className="text-[10px] text-zinc-600">{(att.size / 1024).toFixed(1)} KB</div>}
                      </div>
                      <Download size={12} className="text-zinc-600 group-hover:text-sky-400 shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {(replyMode || replyAllMode) && (
        <div className="shrink-0 px-4 py-3 border-t border-zinc-800/50 bg-zinc-900/30 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-zinc-600">
              {replyAllMode ? 'Répondre à tous' : 'Répondre'} à {replyAllMode ? allRecipients.join(', ') : (isSentMessage ? displayRecipients?.split(',')[0] || 'destinataire' : senderName)}
            </span>
            <button onClick={() => { setReplyMode(false); setReplyAllMode(false); setReplyBody(''); }} className="text-zinc-600 hover:text-zinc-400">
              <X size={12} />
            </button>
          </div>
          <textarea value={replyBody} onChange={e => { setReplyBody(e.target.value); }}
            placeholder="Votre réponse..." rows={3} autoFocus
            className="w-full bg-zinc-950 border border-zinc-700/80 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-sky-500 resize-none text-zinc-200" />
          <div className="flex justify-end gap-2">
            <button onClick={() => { setReplyMode(false); setReplyAllMode(false); setReplyBody(''); }}
              className="px-3 py-1.5 bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs rounded-xl">Annuler</button>
            <button onClick={replyAllMode ? onReplyAll : onReply} disabled={!replyBody.trim()}
              className="px-4 py-1.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white text-xs rounded-xl flex items-center gap-1.5">
              <Send size={11} /> Envoyer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ComposePanel (avec chargement/sauvegarde brouillons, transfert) ─────────
function ComposePanel({ compose, setCompose, templates, onSend, onCancel, openDropdown, setOpenDropdown, draftToLoad }) {
  const setField = (k, v) => setCompose(p => ({ ...p, [k]: v }));
  const [draftId] = useState(() => crypto.randomUUID?.() || Date.now().toString());

  // Charger un brouillon si fourni
  useEffect(() => {
    if (draftToLoad) {
      setCompose(prev => ({ ...prev, ...draftToLoad, id: draftToLoad.id }));
    } else if (!compose.id) {
      setCompose(prev => ({ ...prev, id: draftId }));
    }
  }, [draftToLoad]);

  // Sauvegarde auto dans localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (compose.subject || compose.body || compose.to.length) {
        saveDraft({ ...compose, id: compose.id || draftId, updatedAt: Date.now() });
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [compose]);

  // Raccourci Ctrl+Entrée pour envoyer
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        onSend();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSend]);

  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-lg font-black text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-cyan-400 uppercase tracking-tighter">
          Nouveau message
        </h4>
        <div className="flex items-center gap-2">
          <div className="flex bg-zinc-900/80 border border-zinc-700 rounded-xl p-0.5 gap-0.5">
            {[{id:'interne',label:'Interne',icon:MessageCircle},{id:'email',label:'Email',icon:AtSign}].map(c => (
              <button key={c.id} onClick={() => setField('canal', c.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold uppercase rounded-[10px] transition-all ${
                  compose.canal === c.id
                    ? c.id === 'email' ? 'bg-violet-600 text-white' : 'bg-sky-600 text-white'
                    : 'text-zinc-500 hover:text-zinc-300'
                }`}>
                <c.icon size={11} />{c.label}
              </button>
            ))}
          </div>
          <div className="relative" onMouseDown={e => e.stopPropagation()}>
            <button onClick={() => setOpenDropdown(d => d === 'templates' ? null : 'templates')}
              className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900/80 border border-zinc-700 text-zinc-500 hover:text-zinc-300 text-xs rounded-xl">
              <FileText size={12} /> Templates
            </button>
            {openDropdown === 'templates' && (
              <div className="absolute right-0 top-full mt-1 w-64 bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl z-50 py-1">
                {templates.map(t => (
                  <button key={t.id} onClick={() => { setCompose(p => ({ ...p, subject: t.subject, body: t.body, template: t.id })); setOpenDropdown(null); }}
                    className="w-full px-4 py-3 text-left hover:bg-zinc-800 border-b border-zinc-800/40 last:border-0">
                    <div className="text-xs text-zinc-200 font-medium">{t.name}</div>
                    <div className="text-[10px] text-zinc-600 mt-0.5 truncate">{t.subject}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-4 bg-zinc-900/40 border border-zinc-800/50 rounded-2xl p-5">
        <div>
          <label className="block text-[10px] uppercase text-zinc-500 mb-1.5">
            Destinataires * ({compose.to.length} sélectionné(s))
          </label>
          <DestinataireInput
  label="Destinataires *"
  recipients={compose.to}
  onChange={(newTo) => setField('to', newTo)}
  canal={compose.canal}
  placeholder={compose.canal === 'email' 
    ? "Saisir une adresse email ou un nom..." 
    : "Saisir un nom ou email d'utilisateur..."}
/>
        </div>

        {compose.canal === 'email' && (
          <details>
            <summary className="cursor-pointer text-[10px] text-zinc-600 hover:text-zinc-400 uppercase tracking-widest">CC / BCC</summary>
            <div className="mt-3 space-y-3 pl-3 border-l border-zinc-800">
              {[['cc','CC'],['bcc','BCC']].map(([k,l]) => (
                <div key={k}>
                  <label className="block text-[10px] uppercase text-zinc-600 mb-1">{l}</label>
                  <input type="text" value={compose[k].join(', ')}
                    onChange={e => setField(k, e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    className="w-full bg-zinc-950 border border-zinc-700/70 rounded-xl px-4 py-2 text-sm outline-none focus:border-sky-500 text-zinc-200" />
                </div>
              ))}
            </div>
          </details>
        )}

        <div>
          <label className="block text-[10px] uppercase text-zinc-500 mb-1.5">Sujet *</label>
          <input type="text" value={compose.subject} onChange={e => setField('subject', e.target.value)}
            placeholder="Sujet du message"
            className="w-full bg-zinc-950 border border-zinc-700/70 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-sky-500 text-zinc-200" />
        </div>

        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-[10px] uppercase text-zinc-500 mb-1.5">Priorité</label>
            <select value={compose.priority} onChange={e => setField('priority', e.target.value)}
              className="bg-zinc-950 border border-zinc-700/70 rounded-xl px-3 py-2 text-sm outline-none focus:border-sky-500 text-zinc-200">
              {[['low','Basse'],['normal','Normale'],['high','Haute'],['urgent','Urgente']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          {compose.canal === 'interne' && (
            <>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={compose.encrypted} onChange={e => setField('encrypted', e.target.checked)} className="accent-sky-500 w-4 h-4" />
                <Lock size={13} className={compose.encrypted ? 'text-emerald-400' : 'text-zinc-600'} />
                <span className="text-sm text-zinc-400">Chiffrer</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={compose.requireReadReceipt} onChange={e => setField('requireReadReceipt', e.target.checked)} className="accent-sky-500 w-4 h-4" />
                <Eye size={13} className={compose.requireReadReceipt ? 'text-sky-400' : 'text-zinc-600'} />
                <span className="text-sm text-zinc-400">Accusé</span>
              </label>
            </>
          )}
        </div>

        <div>
          <label className="block text-[10px] uppercase text-zinc-500 mb-1.5">Message *</label>
          <textarea value={compose.body} onChange={e => setField('body', e.target.value)}
            placeholder="Votre message..." rows={10}
            className="w-full bg-zinc-950 border border-zinc-700/70 rounded-xl px-4 py-3 text-sm outline-none focus:border-sky-500 resize-none font-mono text-zinc-200" />
          <div className="flex justify-between text-[10px] text-zinc-700 mt-1">
            <span>{compose.body.length} car.</span>
            {compose.template && <span className="text-purple-400">Template</span>}
          </div>
        </div>

        {compose.attachments.length > 0 && (
          <div className="space-y-1.5">
            {compose.attachments.map((f, i) => (
              <div key={i} className="flex items-center gap-3 px-3 py-2 bg-zinc-950 border border-zinc-700 rounded-xl">
                <Paperclip size={11} className="text-zinc-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-zinc-300 truncate">{f.name}</div>
                  <div className="text-[10px] text-zinc-700">{(f.size / 1024).toFixed(1)} KB</div>
                </div>
                <button onClick={() => setField('attachments', compose.attachments.filter((_,j) => j !== i))} className="p-1 hover:bg-red-900/20 text-red-500 rounded">
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
        <label className="flex items-center justify-center gap-2 px-4 py-3 bg-zinc-950 border-2 border-zinc-700 border-dashed rounded-xl hover:bg-zinc-900 cursor-pointer transition-colors">
          <Upload size={13} className="text-zinc-600" />
          <span className="text-xs text-zinc-600">Ajouter des fichiers (max 10 MB)</span>
          <input type="file" multiple className="hidden" onChange={e => {
            const files = Array.from(e.target.files).filter(f => f.size <= 10 * 1024 * 1024);
            setField('attachments', [...compose.attachments, ...files]);
          }} />
        </label>

        <div className="flex flex-wrap gap-2">
          {[
            { label: '+ Merci',       text: 'Merci pour votre message.' },
            { label: '+ Bien reçu',   text: 'Message bien reçu, je reviens vers vous.' },
            { label: '+ Cordialement',text: '\n\nCordialement,\n[Votre nom]' },
            { label: '+ Rappel',      text: 'Je me permets de revenir vers vous...' },
          ].map((q, i) => (
            <button key={i} onClick={() => setField('body', compose.body + '\n\n' + q.text)}
              className="px-3 py-1.5 bg-zinc-950 border border-zinc-700 rounded-lg text-[11px] text-zinc-500 hover:text-zinc-300 hover:border-sky-700/40 transition-all">
              {q.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-[10px] text-zinc-700 flex items-center gap-1.5">
          {(compose.subject || compose.body) && <><div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />Brouillon sauvegardé</>}
        </div>
        <div className="flex gap-2 ml-auto">
          <button onClick={() => { deleteDraft(compose.id); onCancel(); }} className="px-4 py-2 bg-zinc-900 border border-zinc-700 text-zinc-400 text-xs rounded-xl hover:bg-zinc-800">Annuler</button>
          <button onClick={() => saveDraft(compose)}
            className="px-4 py-2 bg-zinc-900 border border-zinc-700 text-zinc-400 text-xs rounded-xl hover:bg-zinc-800 flex items-center gap-1.5">
            <Bookmark size={11} /> Sauvegarder brouillon
          </button>
          <button onClick={onSend} disabled={!compose.to.length || !compose.subject || !compose.body}
            className={`px-5 py-2 text-white text-xs font-medium rounded-xl flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-all ${
              compose.canal === 'email'
                ? 'bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400'
                : 'bg-gradient-to-r from-sky-600    to-sky-500    hover:from-sky-500    hover:to-sky-400'
            }`}>
            <Send size={11} /> Envoyer (Ctrl+Enter)
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── QuickMessageForm (inchangé) ─────────────────────────────────────────────
function QuickMessageForm({ recipient, closeMessagerie, sendInternalMessage, showToast }) {
  const [subject, setSubject] = useState(`Contact : ${recipient.name || recipient.email || '?'}`);
  const [body,    setBody]    = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      await sendInternalMessage(subject, body, [recipient.id || recipient.email]);
      showToast('Message envoyé', 'success');
      closeMessagerie();
    } catch (e) {
      showToast(`Erreur : ${e.message}`, 'error');
    } finally { setSending(false); }
  };

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center gap-3 p-3 bg-zinc-900/50 rounded-xl border border-zinc-800/50">
        <div className="w-8 h-8 rounded-full bg-sky-500/10 flex items-center justify-center text-sky-400 text-xs font-bold border border-sky-500/20 shrink-0">
          {(recipient.name || '?').slice(0, 2).toUpperCase()}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold text-zinc-200 truncate">{recipient.name || recipient.email || '?'}</div>
          {recipient.id && <div className="text-[10px] text-zinc-600 font-mono truncate">{recipient.id}</div>}
        </div>
      </div>
      <div>
        <label className="text-[10px] text-zinc-500 uppercase block mb-1.5">Sujet</label>
        <input value={subject} onChange={e => setSubject(e.target.value)}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm focus:border-sky-500 outline-none text-zinc-200" />
      </div>
      <div>
        <label className="text-[10px] text-zinc-500 uppercase block mb-1.5">Message</label>
        <textarea value={body} onChange={e => setBody(e.target.value)} rows={5} autoFocus
          placeholder="Votre message..."
          className="w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2.5 text-sm focus:border-sky-500 outline-none resize-none text-zinc-200" />
      </div>
      <div className="flex justify-end gap-2">
        <button onClick={closeMessagerie} className="px-4 py-2 bg-zinc-800 border border-zinc-700 text-zinc-400 text-xs rounded-xl hover:bg-zinc-700">Annuler</button>
        <button onClick={handleSend} disabled={sending || !body.trim()}
          className="px-5 py-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-40 text-white text-xs font-bold rounded-xl flex items-center gap-1.5">
          <Send size={12} />{sending ? 'Envoi...' : 'Envoyer'}
        </button>
      </div>
    </div>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
export default function PanneauMessagerie() {
  const { isOpen, recipient, closeMessagerie, sendInternalMessage } = useMessagerieStore();

  const [canal,           setCanal]           = useState('interne');
  const [view,            setView]            = useState('inbox');
  const [messages,        setMessages]        = useState([]);
  const [selectedMsg,     setSelectedMsg]     = useState(null);
  const [loading,         setLoading]         = useState(false);
  const [unreadInternal,  setUnreadInternal]  = useState(0);
  const [unreadEmail,     setUnreadEmail]     = useState(0);
  const [search,          setSearch]          = useState('');
  const [showFilters,     setShowFilters]     = useState(false);
  const [filters,         setFilters]         = useState({ priority: 'all', hasAttachments: false, labels: [] });
  const [pagination,      setPagination]      = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [compose,         setCompose]         = useState(EMPTY_COMPOSE);
  const [threadMsgs,      setThreadMsgs]      = useState(null);
  const [showThread,      setShowThread]      = useState(false);
  const [replyMode,       setReplyMode]       = useState(false);
  const [replyAllMode,    setReplyAllMode]    = useState(false);
  const [replyBody,       setReplyBody]       = useState('');
  const [typingUsers,     setTypingUsers]     = useState(new Set());
  const [notifEnabled,    setNotifEnabled]    = useState(false);
  const [toast,           setToast]           = useState(null);
  const [openDropdown,    setOpenDropdown]    = useState(null);
  const [drafts,          setDrafts]          = useState([]);
  const [draftToLoad,     setDraftToLoad]     = useState(null);

  const showToast = useCallback((msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // Charger la liste des brouillons
  const refreshDrafts = useCallback(() => {
    setDrafts(getDrafts().sort((a,b) => (b.updatedAt || 0) - (a.updatedAt || 0)));
  }, []);

  useEffect(() => {
    refreshDrafts();
  }, []);

  // Fermer les dropdowns au clic extérieur
  useEffect(() => {
    const close = () => setOpenDropdown(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  // Gestion du WebSocket pour l'interne
  useEffect(() => {
    if (canal !== 'interne') return;
    messagerieSocket.connect();

    const doLoad = () => {
      setLoading(true);
      if (view === 'sent') messagerieSocket.getSent?.({ page: pagination.page, limit: pagination.limit });
      else messagerieSocket.getInbox?.({ page: pagination.page, limit: pagination.limit });
      messagerieSocket.getUnreadCount?.();
    };
    doLoad();

    const u1 = messagerieSocket.on('inbox', d => {
      if (view === 'inbox') {
        setMessages((d.messages || []).map(m => normalizeMsg(m, 'interne')));
        setPagination(p => ({ ...p, ...(d.pagination || {}) }));
        setLoading(false);
      }
    });
    const u2 = messagerieSocket.on('sent', d => {
      if (view === 'sent') {
        setMessages((d.messages || []).map(m => normalizeMsg(m, 'interne')));
        setPagination(p => ({ ...p, ...(d.pagination || {}) }));
        setLoading(false);
      }
    });
    const u3 = messagerieSocket.on('unread-count', d => setUnreadInternal(typeof d === 'number' ? d : d?.count ?? 0));
    const u4 = messagerieSocket.on('user-typing', d => {
      setTypingUsers(prev => new Set([...prev, d.userId]));
      setTimeout(() => setTypingUsers(prev => { const n = new Set(prev); n.delete(d.userId); return n; }), 3000);
    });

    return () => { u1?.(); u2?.(); u3?.(); u4?.(); };
  }, [canal, view, pagination.page]);

  // Chargement email REST
  const loadEmailApi = useCallback(async () => {
    if (canal !== 'email' || view === 'compose') return;
    setLoading(true);
    try {
      let endpoint = '/inbox';
      let extraParams = {};
      let isSentView = false;
      switch (view) {
        case 'sent':
          endpoint = '/sent';
          isSentView = true;
          break;
        case 'starred':
          endpoint = '/inbox';
          extraParams = { isStarred: 'true' };
          break;
        case 'archived':
          endpoint = '/inbox';
          extraParams = { isArchived: 'true' };
          break;
        default: break;
      }
      const { data } = await api.get(`/superadmin/email${endpoint}`, {
        params: { page: pagination.page, limit: pagination.limit, ...extraParams },
      });
      const rawEmails = data.emails || [];
      const normalized = rawEmails.map(m => {
        if (isSentView) {
          return {
            _id: m._id,
            canal: 'email',
            type: 'sent',
            subject: m.subject || '(sans sujet)',
            body: m.bodyText || m.body || '',
            bodyHtml: m.bodyHtml || '',
            from: { name: 'Moi', email: process.env.REACT_APP_SUPERADMIN_EMAIL || 'admin@pesacore.fr' },
            to: Array.isArray(m.to) ? m.to.map(t => typeof t === 'string' ? { email: t, name: t.split('@')[0] } : t) : [],
            isRead: true,
            isStarred: m.isStarred || false,
            isArchived: false,
            labels: m.labels || [],
            attachments: m.attachments || [],
            date: m.sentAt || m.createdAt || m.updatedAt,
          };
        }
        return normalizeMsg(m, 'email');
      });
      setMessages(normalized);
      setPagination(p => ({ ...p, ...(data.pagination || {}) }));
      if (!isSentView) {
        const { data: ucData } = await api.get('/superadmin/email/unread-count');
        setUnreadEmail(ucData.count || 0);
      }
    } catch (err) {
      console.error(err);
      showToast(`Erreur chargement ${view}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [canal, view, pagination.page, pagination.limit, showToast]);

  useEffect(() => {
    loadEmailApi();
  }, [loadEmailApi]);

  // Gestion sélection message
  const handleSelect = async (msg) => {
    setSelectedMsg(msg);
    setReplyMode(false); setReplyAllMode(false); setReplyBody('');
    setShowThread(false); setThreadMsgs(null);
    if (canal === 'interne') {
      if (!msg.isRead) messagerieSocket.markAsRead?.(msg._id);
      if (msg.threadId) {
        try {
          const { data } = await api.get(`/admin/messagerie/thread/${msg.threadId}`);
          setThreadMsgs((data.messages || []).map(m => normalizeMsg(m, 'interne')));
        } catch {}
      }
    } else {
      if (!msg.isRead) {
        await api.put(`/superadmin/email/${msg._id}/read`, { isRead: true });
        setMessages(prev => prev.map(m => m._id === msg._id ? { ...m, isRead: true } : m));
        setSelectedMsg(p => ({ ...p, isRead: true }));
      }
    }
  };

  const handleClose = () => {
    setSelectedMsg(null); setReplyMode(false); setReplyAllMode(false); setReplyBody('');
    setShowThread(false); setThreadMsgs(null); setOpenDropdown(null);
  };

  const toggleStar = async (id, e) => {
    e?.stopPropagation();
    if (canal === 'interne') await api.put(`/admin/messagerie/${id}/star`);
    else await api.put(`/superadmin/email/${id}/star`);
    setMessages(prev => prev.map(m => m._id === id ? { ...m, isStarred: !m.isStarred } : m));
    if (selectedMsg?._id === id) setSelectedMsg(p => ({ ...p, isStarred: !p.isStarred }));
  };

  const addLabel = async (id, labelId) => {
    if (canal === 'interne') await api.post(`/admin/messagerie/${id}/label`, { labelId });
    else await api.put(`/superadmin/email/${id}/label`, { label: labelId });
    setMessages(prev => prev.map(m => m._id === id ? { ...m, labels: [...new Set([...(m.labels || []), labelId])] } : m));
    setOpenDropdown(null);
  };

  const archiveMsg = async (id) => {
    if (canal === 'interne') messagerieSocket.archiveMessage?.(id);
    else await api.put(`/superadmin/email/${id}/archive`);
    setMessages(prev => prev.filter(m => m._id !== id));
    if (selectedMsg?._id === id) handleClose();
    setOpenDropdown(null);
  };

  const deleteMsg = async (id) => {
    if (!window.confirm('Supprimer définitivement ce message ?')) return;
    if (canal === 'interne') messagerieSocket.deleteMessage?.(id);
    else await api.delete(`/superadmin/email/${id}`);
    setMessages(prev => prev.filter(m => m._id !== id));
    if (selectedMsg?._id === id) handleClose();
    setOpenDropdown(null);
  };

  const sendSMS = async (phone, body) => {
    try {
      await api.post('/admin/messagerie/sms', { to: phone, body });
      showToast('SMS envoyé', 'success');
    } catch {
      showToast('Erreur SMS', 'error');
    }
    setOpenDropdown(null);
  };

  const handleReply = async () => {
    if (!replyBody.trim()) return;
    if (canal === 'interne') {
      messagerieSocket.replyToMessage?.(selectedMsg._id, replyBody);
    } else {
      await api.post(`/superadmin/email/reply/${selectedMsg._id}`, { bodyText: replyBody });
      showToast('Réponse envoyée', 'success');
    }
    setReplyMode(false); setReplyBody('');
  };

  const handleReplyAll = async () => {
    if (!replyBody.trim()) return;
    // Construire la liste des destinataires (to + cc + from sauf expéditeur)
    const allRecipients = [];
    if (selectedMsg.to) allRecipients.push(...selectedMsg.to.map(t => typeof t === 'object' ? t.email : t));
    if (selectedMsg.cc) allRecipients.push(...selectedMsg.cc.map(t => typeof t === 'object' ? t.email : t));
    if (selectedMsg.from?.email && selectedMsg.from.email !== process.env.REACT_APP_SUPERADMIN_EMAIL) {
      allRecipients.push(selectedMsg.from.email);
    }
    const unique = [...new Set(allRecipients.filter(Boolean))];
    if (canal === 'interne') {
      // Pour l'interne, il faudrait mapper les emails vers des IDs, mais pas implémenté ici
      showToast('Reply All non supporté pour l’interne', 'error');
    } else {
      await api.post(`/superadmin/email/reply/${selectedMsg._id}`, { bodyText: replyBody, to: unique });
      showToast('Réponse à tous envoyée', 'success');
    }
    setReplyAllMode(false); setReplyBody('');
  };

  const handleTransfer = (msg) => {
    const newDraft = {
      ...EMPTY_COMPOSE,
      id: crypto.randomUUID?.(),
      canal: msg.canal,
      subject: `TR: ${msg.subject}`,
      body: `\n\n-------- Message transféré --------\nDe: ${typeof msg.from === 'object' ? msg.from.name || msg.from.email : msg.from}\nDate: ${fmtDate(msg.date)}\nSujet: ${msg.subject}\n\n${msg.body}`,
    };
    setCompose(newDraft);
    setView('compose');
    setDraftToLoad(null);
  };

  const handleSend = async () => {
    if (!compose.to.length || !compose.subject || !compose.body) {
      showToast('Destinataire, sujet et message requis', 'error');
      return;
    }
    // Validation supplémentaire pour éviter mélange canal/identifiant
    if (compose.canal === 'email') {
      const invalid = compose.to.some(t => !t.includes('@'));
      if (invalid) {
        showToast('Les destinataires email doivent être des adresses valides', 'error');
        return;
      }
    } else {
      // Pour interne, on s'assure que ce sont des IDs (pas d'email)
      const invalid = compose.to.some(t => t.includes('@'));
      if (invalid) {
        showToast('Les destinataires internes doivent être des utilisateurs (pas des emails externes)', 'error');
        return;
      }
    }
    try {
      if (compose.canal === 'email') {
        await api.post('/superadmin/email/send', {
          to: compose.to,
          cc: compose.cc,
          bcc: compose.bcc,
          subject: compose.subject,
          bodyText: compose.body,
        });
      } else {
        await sendInternalMessage(compose.subject, compose.body, compose.to);
      }
      // Supprimer le brouillon après envoi
      if (compose.id) deleteDraft(compose.id);
      setCompose(EMPTY_COMPOSE);
      setView('inbox');
      showToast('Message envoyé', 'success');
    } catch (err) {
      showToast(`Erreur : ${err.message || 'Envoi échoué'}`, 'error');
    }
  };

  const triggerPoll = async () => {
    try {
      const { data } = await api.post('/superadmin/email/poll');
      showToast(`${data.newEmails ?? 0} nouveau(x) email(s)`, 'info');
      if (canal === 'email') loadEmailApi();
    } catch {
      showToast('Erreur lors du poll IMAP', 'error');
    }
  };

  const switchCanal = (c) => { setCanal(c); setView('inbox'); setSelectedMsg(null); setMessages([]); setPagination(p => ({ ...p, page: 1 })); };
  const switchView  = (v) => { setView(v); setSelectedMsg(null); setPagination(p => ({ ...p, page: 1 })); if (v === 'drafts') refreshDrafts(); };

  // Filtrage local
  const filtered = useMemo(() => {
    let r = [...messages];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(m =>
        m.subject?.toLowerCase().includes(q) ||
        m.body?.toLowerCase().includes(q) ||
        (typeof m.from === 'object' ? `${m.from?.name || ''} ${m.from?.email || ''}` : String(m.from)).toLowerCase().includes(q)
      );
    }
    if (filters.priority !== 'all') r = r.filter(m => m.metadata?.priority === filters.priority);
    if (filters.hasAttachments)     r = r.filter(m => m.attachments?.length > 0);
    if (filters.labels.length > 0)  r = r.filter(m => filters.labels.some(l => m.labels?.includes(l)));
    return r;
  }, [messages, search, filters]);

  return (
    <div className="flex flex-col h-full min-h-0">
      {toast && (
        <div className={`fixed bottom-4 right-4 z-[250] px-4 py-3 rounded-xl border text-xs font-mono shadow-2xl ${
          toast.type === 'success' ? 'bg-emerald-950/95 border-emerald-700/60 text-emerald-300' :
          toast.type === 'error'   ? 'bg-red-950/95 border-red-700/60 text-red-300' :
          'bg-sky-950/95 border-sky-700/60 text-sky-300'
        }`}>
          {toast.msg}
        </div>
      )}

      {/* HEADER */}
      <div className="shrink-0 px-4 sm:px-6 pt-5 pb-3 border-b border-zinc-800/60 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-xl sm:text-2xl font-black tracking-tighter text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-cyan-400 flex items-center gap-2">
              Messagerie {notifEnabled && <Bell size={14} className="text-emerald-400 animate-pulse" />}
            </h2>
            <p className="text-[10px] uppercase tracking-widest text-zinc-600 mt-0.5 flex items-center gap-2">
              {unreadInternal > 0 && <span className="text-sky-400">{unreadInternal} interne</span>}
              {unreadEmail    > 0 && <span className="text-violet-400">{unreadEmail} email</span>}
              {unreadInternal + unreadEmail === 0 && <span>Tout lu</span>}
            </p>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {canal === 'email' && (
              <button onClick={triggerPoll} title="Vérifier nouveaux emails"
                className="p-2 bg-zinc-900/80 border border-zinc-700 text-zinc-500 hover:text-violet-300 rounded-xl transition-all">
                <RotateCw size={13} />
              </button>
            )}
            <button onClick={() => { setView('compose'); setDraftToLoad(null); setCompose({ ...EMPTY_COMPOSE, id: crypto.randomUUID?.() }); }}
              className="flex items-center gap-1.5 px-3 py-2 bg-sky-600/15 hover:bg-sky-600/30 border border-sky-500/30 text-sky-300 font-bold text-xs uppercase tracking-widest rounded-xl transition-all">
              <Send size={13} /><span className="hidden sm:inline">Nouveau</span>
            </button>
            <button onClick={() => notifEnabled ? setNotifEnabled(false) : Notification.requestPermission().then(p => setNotifEnabled(p === 'granted'))}
              className={`p-2 rounded-xl border transition-all ${notifEnabled ? 'bg-emerald-600/20 text-emerald-400 border-emerald-500/30' : 'bg-zinc-900/80 text-zinc-500 border-zinc-700'}`}>
              {notifEnabled ? <Bell size={13} /> : <BellOff size={13} />}
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {[
            { id: 'interne', label: 'Interne', icon: MessageCircle, badge: unreadInternal, activeClass: 'bg-sky-600/20 border-sky-500/40 text-sky-300' },
            { id: 'email',   label: 'Email',   icon: AtSign,        badge: unreadEmail,    activeClass: 'bg-violet-600/20 border-violet-500/40 text-violet-300' },
          ].map(c => (
            <button key={c.id} onClick={() => switchCanal(c.id)}
              className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold uppercase tracking-wider rounded-xl border transition-all ${
                canal === c.id ? c.activeClass : 'bg-zinc-900/60 border-zinc-700 text-zinc-500 hover:text-zinc-300 hover:border-zinc-600'
              }`}>
              <c.icon size={12} />{c.label}
              {c.badge > 0 && (
                <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${c.id === 'email' ? 'bg-violet-900/60 text-violet-300' : 'bg-sky-900/60 text-sky-300'}`}>
                  {c.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {view !== 'compose' && (
          <div className="flex items-center bg-zinc-900/60 border border-zinc-800/50 p-0.5 rounded-xl overflow-x-auto scrollbar-none gap-0.5">
            {[
              { id: 'inbox',    icon: Mail,    label: 'Reçus'    },
              { id: 'sent',     icon: Send,    label: 'Envoyés'  },
              { id: 'starred',  icon: Star,    label: 'Favoris'  },
              { id: 'archived', icon: Archive, label: 'Archives' },
              { id: 'drafts',   icon: Bookmark,label: 'Brouillons' },
            ].map(tab => (
              <button key={tab.id} onClick={() => switchView(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded-[10px] shrink-0 transition-all ${
                  view === tab.id
                    ? canal === 'email'
                      ? 'bg-gradient-to-r from-violet-600 to-violet-500 text-white shadow-sm'
                      : 'bg-gradient-to-r from-sky-600    to-sky-500    text-white shadow-sm'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50'
                }`}>
                <tab.icon size={12} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* RECHERCHE + FILTRES (sauf pour compose) */}
      {view !== 'compose' && view !== 'drafts' && (
        <div className="shrink-0 px-4 sm:px-6 py-2 flex items-center gap-2 border-b border-zinc-800/40 bg-black/10">
          <div className="relative flex-1">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
              className="w-full pl-8 pr-3 py-2 text-xs bg-zinc-900/60 border border-zinc-800/70 text-zinc-300 placeholder-zinc-700 outline-none focus:border-sky-500/40 rounded-lg font-mono" />
          </div>
          <button onClick={() => setShowFilters(v => !v)}
            className={`shrink-0 p-2 border rounded-lg transition-all ${showFilters ? 'bg-sky-600/20 border-sky-500/40 text-sky-400' : 'bg-zinc-900/60 border-zinc-700 text-zinc-600 hover:text-zinc-400'}`}>
            <Filter size={13} />
          </button>
        </div>
      )}

      {showFilters && view !== 'compose' && view !== 'drafts' && (
        <div className="shrink-0 mx-4 sm:mx-6 my-2 bg-zinc-900/70 border border-zinc-800/50 p-3 rounded-xl space-y-2.5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase text-zinc-600">Priorité</span>
              <select value={filters.priority} onChange={e => setFilters(p => ({ ...p, priority: e.target.value }))}
                className="bg-zinc-950 border border-zinc-700 px-2 py-1 text-xs rounded-lg text-zinc-300 outline-none">
                {[['all','Toutes'],['urgent','Urgent'],['high','Haute'],['normal','Normale']].map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-1.5 text-xs text-zinc-500 cursor-pointer">
              <input type="checkbox" checked={filters.hasAttachments} onChange={e => setFilters(p => ({ ...p, hasAttachments: e.target.checked }))} className="accent-sky-500 w-3.5 h-3.5" />
              Pièces jointes
            </label>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {LABELS.map(l => (
              <button key={l.id} onClick={() => setFilters(p => ({ ...p, labels: p.labels.includes(l.id) ? p.labels.filter(x => x !== l.id) : [...p.labels, l.id] }))}
                className={`px-2.5 py-1 text-[10px] rounded-lg border transition-all ${filters.labels.includes(l.id) ? l.cls : 'bg-zinc-900 border-zinc-700 text-zinc-600 hover:text-zinc-300'}`}>
                {l.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* CORPS PRINCIPAL */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {view !== 'compose' && view !== 'drafts' && (
          <div className="h-full flex flex-col lg:flex-row">
            <div className={`${selectedMsg ? 'hidden lg:flex' : 'flex'} flex-col lg:w-2/5 xl:w-1/3 min-h-0 border-r border-zinc-800/40`}>
              <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-2 text-[9px] font-black uppercase tracking-widest border-b bg-zinc-900/40 border-zinc-800/50 text-zinc-600 shrink-0">
                <div className="col-span-1" /><div className="col-span-1 text-center">⚑</div>
                <div className="col-span-4">De</div><div className="col-span-4">Sujet</div>
                <div className="col-span-2 text-right">Date</div>
              </div>
              <div className="flex-1 overflow-y-auto divide-y divide-zinc-800/30">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3 text-zinc-600">
                    <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-sky-500" />
                    <span className="text-xs">Chargement...</span>
                  </div>
                ) : filtered.length > 0 ? (
                  filtered.map(msg => (
                    <MessageRow key={msg._id} msg={msg} canal={canal}
                      isSelected={selectedMsg?._id === msg._id}
                      onSelect={handleSelect} onStar={toggleStar} />
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 gap-3 text-zinc-700">
                    <Mail size={36} className="opacity-30" />
                    <span className="text-xs">Aucun message</span>
                  </div>
                )}
              </div>
              {pagination.pages > 1 && (
                <div className="shrink-0 flex items-center justify-between px-4 py-2 border-t border-zinc-800/50 bg-zinc-900/30">
                  <span className="text-[10px] text-zinc-700">{pagination.page}/{pagination.pages}</span>
                  <div className="flex gap-1">
                    {[[-1, ChevronLeft],[1, ChevronRight]].map(([delta, Ic]) => (
                      <button key={delta} onClick={() => setPagination(p => ({ ...p, page: p.page + delta }))}
                        disabled={delta === -1 ? pagination.page === 1 : pagination.page === pagination.pages}
                        className="p-1.5 border border-zinc-700 hover:bg-zinc-800 disabled:opacity-30 rounded-lg">
                        <Ic size={12} />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {selectedMsg && (
              <div className="flex flex-col flex-1 min-h-0 min-w-0">
                <MessageDetail
                  msg={selectedMsg} canal={canal}
                  threadMsgs={threadMsgs} showThread={showThread} setShowThread={setShowThread}
                  replyMode={replyMode} setReplyMode={setReplyMode}
                  replyAllMode={replyAllMode} setReplyAllMode={setReplyAllMode}
                  replyBody={replyBody} setReplyBody={setReplyBody}
                  typingUsers={typingUsers}
                  openDropdown={openDropdown} setOpenDropdown={setOpenDropdown}
                  onClose={handleClose} onStar={toggleStar}
                  onAddLabel={addLabel} onArchive={archiveMsg} onDelete={deleteMsg}
                  onSendSMS={sendSMS} onReply={handleReply} onReplyAll={handleReplyAll} onTransfer={handleTransfer}
                />
              </div>
            )}
          </div>
        )}

        {view === 'drafts' && (
          <div className="h-full overflow-y-auto p-4 space-y-3">
            <h3 className="text-sm font-bold text-sky-400 uppercase tracking-wider">Brouillons sauvegardés</h3>
            {drafts.length === 0 ? (
              <div className="text-center py-10 text-zinc-600 text-sm">Aucun brouillon</div>
            ) : (
              drafts.map(draft => (
                <div key={draft.id} className="bg-zinc-900/40 border border-zinc-700 rounded-xl p-4 flex flex-wrap justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${draft.canal === 'email' ? 'bg-violet-900/40 text-violet-300' : 'bg-sky-900/40 text-sky-300'}`}>
                        {draft.canal === 'email' ? 'Email' : 'Interne'}
                      </span>
                      <span className="text-sm font-medium text-zinc-200 truncate">{draft.subject || '(sans sujet)'}</span>
                    </div>
                    <div className="text-xs text-zinc-500 truncate mt-1">
                      À: {Array.isArray(draft.to) ? draft.to.join(', ') : ''}
                    </div>
                    <div className="text-[10px] text-zinc-700 mt-1">
                      Modifié le {(() => {
                        try {
                          const date = new Date(draft.updatedAt);
                          if (isNaN(date.getTime())) return 'date inconnue';
                          return format(date, 'dd/MM/yyyy HH:mm');
                        } catch {
                          return 'date inconnue';
                        }
                      })()}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setCompose(draft); setView('compose'); setDraftToLoad(null); }}
                      className="px-3 py-1.5 bg-sky-600/20 text-sky-300 border border-sky-700/40 rounded-lg text-xs">
                      Charger
                    </button>
                    <button onClick={() => { deleteDraft(draft.id); refreshDrafts(); }}
                      className="px-3 py-1.5 bg-red-900/20 text-red-400 border border-red-800/40 rounded-lg text-xs">
                      Supprimer
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {view === 'compose' && (
          <div className="h-full overflow-y-auto px-4 sm:px-6 py-5">
            <ComposePanel
              compose={compose} setCompose={setCompose}
              templates={TEMPLATES}
              onSend={handleSend}
              onCancel={() => { setCompose(EMPTY_COMPOSE); setView('inbox'); setDraftToLoad(null); }}
              openDropdown={openDropdown} setOpenDropdown={setOpenDropdown}
              draftToLoad={draftToLoad}
            />
          </div>
        )}
      </div>

      {/* MODAL MESSAGE RAPIDE */}
      {isOpen && recipient && (
        <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-md p-0 sm:p-4">
          <div className="w-full sm:max-w-lg bg-zinc-950 border border-zinc-700/60 shadow-2xl rounded-t-2xl sm:rounded-2xl overflow-hidden">
            <div className="px-5 py-3.5 border-b border-zinc-800/70 flex items-center justify-between bg-zinc-900/60">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-sky-500 animate-pulse rounded-full" />
                <h3 className="text-sm font-black text-sky-400 uppercase tracking-tighter">Message rapide</h3>
              </div>
              <button onClick={closeMessagerie} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-lg transition">
                <X size={15} />
              </button>
            </div>
            <QuickMessageForm recipient={recipient} closeMessagerie={closeMessagerie}
              sendInternalMessage={sendInternalMessage} showToast={showToast} />
          </div>
        </div>
      )}
    </div>
  );
}